// components/AutomationStats.tsx
"use client";
import React from 'react';
import { IconType } from 'react-icons';
import { 
  FiLayers, 
  FiUsers, 
  FiCode, 
  FiGrid, 
  FiUsers as FiTeam, 
  FiMessageCircle 
} from 'react-icons/fi';
import SectionTitle from './SectionTitle';

// Card data type
interface CardData {
  id: number;
  icon: IconType;
  title: string;
  description: string;
}

// StatCard props type
interface StatCardProps {
  icon: IconType;
  title: string;
  description: string;
}

// Card data with new information
const cardData: CardData[] = [
  {
    id: 1,
    icon: FiLayers,
    title: "Strategy-Led Design",
    description: "We start with the problem, not the screen."
  },
  {
    id: 2,
    icon: FiUsers,
    title: "User-First Approach",
    description: "Every interface decision considers usability, accessibility, and intent."
  },
  {
    id: 3,
    icon: FiCode,
    title: "Design + Development Expertise",
    description: "Our design team understands how digital products are actually built."
  },
  {
    id: 4,
    icon: FiGrid,
    title: "Scalable Design Systems",
    description: "We create reusable systems instead of isolated screens."
  },
  {
    id: 5,
    icon: FiTeam,
    title: "Cross-Functional Team",
    description: "UI/UX, branding, development, and growth capabilities work together."
  },
  {
    id: 6,
    icon: FiMessageCircle,
    title: "Client-First Collaboration",
    description: "Transparent communication, iterative feedback, and clear project milestones."
  }
];

const StatCard: React.FC<StatCardProps> = ({ 
  icon: Icon, 
  title, 
  description
}) => {
  return (
    <div 
      className="bg-[#F3F4F6] p-6 flex-1 min-w-[280px] relative shadow-sm"
      style={{
        clipPath: 'polygon(0 0, calc(100% - 32px) 0, 100% 32px, 100% 100%, 32px 100%, 0 calc(100% - 32px))'
      }}
    >
      {/* Icon Container */}
      <div className="absolute top-8 left-5">
        <div className="bg-white p-2.5 rounded shadow-inner">
          <Icon className="w-6 h-6 text-[#1b1b1b]" />
        </div>
      </div>

      {/* Content */}
      <div className="mt-16 space-y-1">
        <h3 className="text-lg font-semibold text-[#272d57]">{title}</h3>
        <p className=" leading-snug text-[#000]">{description}</p>
      </div>
    </div>
  );
};

const AutomationStats: React.FC = () => {
  return (
    <section className="min-h-screen flex items-center">
      <div className="custom-container  grid grid-cols-1 lg:grid-cols-3 gap-16 w-full">
        
        {/* Header Section - Left Column */}
        <div className="flex items-center">
          <SectionTitle
            title="A Reliable UI/UX Design Company with Global Reach"
            description="Our global presence gives us the on-ground context to deliver UI UX design services USA and international brands can rely on."
            descriptionMaxWidth="xl"
            titleMaxWidth='full'
            align="left"
          />
        </div>

        {/* Stats Cards Container - Right Columns (2/3) */}
        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {cardData.map((card) => (
            <StatCard
              key={card.id}
              icon={card.icon}
              title={card.title}
              description={card.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default AutomationStats;